import { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { AppNavigate, useAppNavigate } from "../lib/route-primitives";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/auth-context";
import { useWedding } from "../contexts/wedding-context";
import { formatWeddingDate } from "@wedding/shared";
import { useActiveEvent } from "../lib/wedding-event";
import { paths } from "../lib/paths";
import { useTurnstile } from "../hooks/use-turnstile";
import { PhoneNumberInput } from "../components/phone";
import { OtpDigits } from "../components/OtpDigits";
import { AuthRegisterForm, BUTTON_CLASS, INPUT_CLASS } from "./auth/AuthRegisterForm";
import { DemoEnter } from "../components/demo-enter";
import { cn } from "../lib/utils";
import { Heart, MapPin, Loader2, ArrowRight, MessageCircle } from "lucide-react";

type Step = "phone" | "otp";
type Mode = "login" | "register";

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

/**
 * İçeriği değişince (login ⇄ register, form ⇄ OTP) yüksekliği yumuşakça
 * animasyonlar — kart zıplamaz. `overflow-hidden` yalnız animasyon SÜRERKEN
 * uygulanır; boştayken görünür kalır ki telefon ülke-seçim popover'ı kırpılmasın.
 * `prefers-reduced-motion` açıksa animasyon devre dışı (yükseklik auto).
 */
function AutoHeight({ children }: { children: React.ReactNode }) {
  const innerRef = useRef<HTMLDivElement>(null);
  const prevRef = useRef<number | undefined>(undefined);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const [animating, setAnimating] = useState(false);
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const measure = useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    const next = el.offsetHeight;
    if (prevRef.current !== undefined && prevRef.current !== next) setAnimating(true);
    prevRef.current = next;
    setHeight(next);
  }, []);

  // Her render sonrası ölç — içerik takası (mod/adım) böylece yakalanır.
  useLayoutEffect(() => { measure(); });

  // Asenkron boyut değişimleri (Turnstile widget mount vb.) için gözlemci.
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const el = innerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  return (
    <div
      style={reduceMotion ? undefined : { height }}
      onTransitionEnd={() => setAnimating(false)}
      className={cn(
        "transition-[height] duration-[420ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
        !reduceMotion && animating ? "overflow-hidden" : "overflow-visible"
      )}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
}

export function AuthPage() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, requestCode, verifyCode } = useAuth();
  const navigate = useAppNavigate();
  const { slug, wedding } = useWedding();
  const location = useLocation();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  // Varsayılan: giriş. QR/davet linkleri `?mode=register` ile doğrudan kayda
  // deep-link edebilir.
  const [mode, setMode] = useState<Mode>(
    () => (new URLSearchParams(location.search).get("mode") === "register" ? "register" : "login")
  );
  const [step, setStep] = useState<Step>("phone");
  // Kademeli açılışın yönü: register sağ sekme (+), login sol sekme (−).
  const [enterDir, setEnterDir] = useState(1);
  const [phoneE164, setPhoneE164] = useState("");
  const [phoneValid, setPhoneValid] = useState(false);
  const [digits, setDigits] = useState<string[]>(Array(6).fill(""));
  const [verificationSid, setVerificationSid] = useState<string | undefined>();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { containerRef, token: turnstileToken, error: turnstileError, reset: resetTurnstile } = useTurnstile();

  // Giriş ekranındaki tarih satırı da en yakın etkinliği gösterir — kına
  // gecesi giren misafir kınanın tarihini görür.
  const activeEvent = useActiveEvent(wedding);
  const formattedDate = useMemo(
    () => (activeEvent ? formatWeddingDate(activeEvent.dateTime, i18n.language) : null),
    [activeEvent, i18n.language]
  );

  if (isAuthenticated) {
    return <AppNavigate to={paths.wedding.home(slug)} replace />;
  }

  const otpCode = digits.join("");

  function mapAuthError(msg: string): string {
    if (msg === "Banned") return t("auth.banned");
    if (msg === "service_unavailable") return t("auth.otpUnavailable");
    return msg;
  }

  async function handleRequestCode(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!phoneValid || !phoneE164) {
      setError(t("auth.invalidPhone"));
      return;
    }
    setLoading(true);
    try {
      if (!turnstileToken) {
        setError(t("common.error"));
        return;
      }
      const result = await requestCode(phoneE164, turnstileToken);
      resetTurnstile();
      if (result.authed) {
        navigate(paths.wedding.home(slug), { replace: true });
      } else {
        setVerificationSid(result.verificationSid);
        setStep("otp");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t("common.unknownError");
      setError(mapAuthError(msg));
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyCode(phoneE164, otpCode, verificationSid);
      navigate(paths.wedding.home(slug), { replace: true });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t("common.unknownError");
      setError(mapAuthError(msg));
    } finally {
      setLoading(false);
    }
  }

  function goBack() {
    setStep("phone");
    setDigits(Array(6).fill(""));
    setError("");
  }

  // Segment toggle — login ⇄ register. Telefon parent'ta paylaşık kaldığı için
  // mod değişince numara korunur (yeniden yazılmaz). Yön, kademeli açılışı besler.
  function switchMode(next: Mode) {
    if (next === mode) return;
    setEnterDir(next === "register" ? 1 : -1);
    setMode(next);
    setStep("phone");
    setDigits(Array(6).fill(""));
    setError("");
  }

  // ─── Shared background layer ───────────────────────────────────
  // Auth page is always visually dark — never use theme CSS vars for text here.
  const bgAuthUrl = wedding?.bg_auth ?? null;

  const background = (
    <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
      <div className="absolute inset-0 bg-[#1b1817]" />
      {bgAuthUrl ? (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${bgAuthUrl})` }}
          />
          <div className="absolute inset-0 bg-[#1b1817]/55 backdrop-blur-[2px]" />
        </>
      ) : (
        <>
          <div className="absolute inset-0" style={{ filter: "blur(80px)" }}>
            <div className="absolute top-[-15%] left-[5%] w-[65%] h-[65%] rounded-full bg-primary/30" />
            <div className="absolute bottom-[-15%] right-[5%] w-[55%] h-[55%] rounded-full bg-primary/20" />
            <div className="absolute top-[25%] right-[15%] w-[40%] h-[45%] rounded-full" style={{ background: "rgba(180,130,95,0.22)" }} />
            <div className="absolute top-[50%] left-[20%] w-[35%] h-[35%] rounded-full" style={{ background: "rgba(120,80,60,0.18)" }} />
          </div>
          <div className="absolute inset-0 bg-[#1b1817]/40" />
        </>
      )}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: "radial-gradient(circle at 2px 2px, rgba(141,107,94,0.15) 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      />
    </div>
  );

  // ─── Shared wedding info snippet (always on dark bg) ───────────
  const weddingMeta = (
    <>
      {formattedDate && (
        <p className="text-primary font-medium mt-2">{formattedDate}</p>
      )}
      {wedding?.location && (
        <div className="flex items-center justify-center gap-2 text-white/60 text-sm mt-1">
          <MapPin className="w-3 h-3 shrink-0" />
          <span>{wedding.location}</span>
        </div>
      )}
    </>
  );

  // ─── Phone step (always on dark bg — use explicit light colors) ─
  const phoneStep = (
    <form onSubmit={handleRequestCode} className="auth-cascade space-y-5" noValidate>
      <div>
        <h2 className="text-xl font-serif text-white mb-1">{t("auth.loginTitle")}</h2>
        <p className="text-sm text-slate-400">{t("auth.loginDesc")}</p>
      </div>
      <div className="space-y-1.5">
        <label htmlFor="auth-phone" className="block text-sm font-medium text-slate-300">
          {t("auth.phoneLabel")}
        </label>
        <PhoneNumberInput
          id="auth-phone"
          variant="dark"
          valueE164={phoneE164}
          autoFocus
          onChange={(e164, meta) => {
            setPhoneE164(e164);
            setPhoneValid(meta.isValid);
            setError("");
          }}
        />
      </div>
      <div ref={containerRef} className="flex justify-center" />
      {(error || turnstileError) && (
        <p id="auth-phone-error" role="alert" className="text-sm text-red-400">
          {error || turnstileError}
        </p>
      )}
      <button
        type="submit"
        disabled={loading || !turnstileToken}
        className="w-full bg-primary hover:bg-primary/90 active:scale-[0.98] motion-reduce:active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-lg transition-[background-color,opacity,transform] shadow-lg shadow-primary/20 flex items-center justify-center gap-2"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <ArrowRight className="w-4 h-4" />
        )}
        {loading ? t("auth.loggingIn") : t("auth.login")}
      </button>
    </form>
  );

  // ─── OTP step ──────────────────────────────────────────────────
  const otpStep = (
    <form onSubmit={handleVerifyCode} className="auth-cascade space-y-5" noValidate>
      <div>
        <div className="flex items-center gap-2 mb-1">
          <MessageCircle className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-serif text-white">{t("auth.verifyTitle")}</h2>
        </div>
        <p className="text-sm text-slate-400">{t("auth.verifyDesc")}</p>
      </div>
      <OtpDigits digits={digits} onChange={setDigits} autoFocus />

      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={loading || otpCode.length < 6}
        className="w-full bg-primary hover:bg-primary/90 active:scale-[0.98] motion-reduce:active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-lg transition-[background-color,opacity,transform] shadow-lg shadow-primary/20 flex items-center justify-center gap-2"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Heart className="w-4 h-4" />
        )}
        {loading ? t("auth.verifying") : t("auth.verify")}
      </button>
      <button
        type="button"
        onClick={goBack}
        className="w-full text-sm text-slate-400 hover:text-primary transition-colors py-2 cursor-pointer"
      >
        {t("auth.wrongNumber")}
      </button>
    </form>
  );

  // ─── Segment toggle: Giriş ⇄ Kayıt (kayan pill göstergesi) ─────
  const modeToggle = (
    <div className="relative mb-6 grid grid-cols-2 rounded-lg bg-white/5 p-1 border border-primary/10">
      {/* Kayan aktif gösterge — renk yerine konum animasyonu (imza detay). */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-md bg-primary shadow-sm shadow-primary/30 transition-transform duration-[380ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] motion-reduce:transition-none"
        style={{ transform: mode === "register" ? "translateX(100%)" : "translateX(0)" }}
      />
      <button
        type="button"
        aria-pressed={mode === "login"}
        onClick={() => switchMode("login")}
        className={cn(
          "relative z-10 rounded-md py-2.5 text-sm font-medium transition-colors duration-200 cursor-pointer",
          mode === "login" ? "text-white" : "text-slate-300 hover:text-white"
        )}
      >
        {t("auth.tabLogin")}
      </button>
      <button
        type="button"
        aria-pressed={mode === "register"}
        onClick={() => switchMode("register")}
        className={cn(
          "relative z-10 rounded-md py-2.5 text-sm font-medium transition-colors duration-200 cursor-pointer",
          mode === "register" ? "text-white" : "text-slate-300 hover:text-white"
        )}
      >
        {t("auth.tabRegister")}
      </button>
    </div>
  );

  const formBody = mode === "login" ? (
    step === "phone" ? phoneStep : otpStep
  ) : (
    <AuthRegisterForm
      phoneE164={phoneE164}
      phoneValid={phoneValid}
      onPhoneChange={(e164, valid) => {
        setPhoneE164(e164);
        setPhoneValid(valid);
        setError("");
      }}
      onSwitchToLogin={() => switchMode("login")}
    />
  );

  // Toggle sabit kalır. Gövde değişince: yükseklik AutoHeight ile, alanlar ise
  // key değişince `.auth-cascade` üzerinden yönlü + kademeli açılır. --enter-x
  // yönü alt öğelere miras kalır.
  const bodyKey = mode === "login" ? `login-${step}` : "register";
  // Canlı demo düğünü: giriş/kayıt yerine tek dokunuşla demo girişi (F7).
  const formContent = wedding?.config?.demo === true ? (
    <DemoEnter
      slug={slug}
      classes={{
        card: "",
        label: "block text-xs font-medium uppercase tracking-widest text-slate-400 mb-2",
        input: INPUT_CLASS,
        button: BUTTON_CLASS,
        muted: "text-slate-400",
        error: "text-red-400",
        heading: "font-serif text-xl text-white",
      }}
    />
  ) : (
    <>
      {modeToggle}
      <AutoHeight>
        <div key={bodyKey} style={{ "--enter-x": `${enterDir * 28}px` } as React.CSSProperties}>
          {formBody}
        </div>
      </AutoHeight>
    </>
  );

  // ─── MOBILE LAYOUT ─────────────────────────────────────────────
  if (!isDesktop) {
    return (
      <div className="relative min-h-screen overflow-hidden">
        {background}
        <div className="fixed top-0 right-0 p-8 opacity-20 pointer-events-none">
          <Heart className="w-16 h-16 text-primary" />
        </div>
        <main className="relative z-10 flex flex-col min-h-screen max-w-md mx-auto w-full px-6 pt-12 pb-8">
          {/* Hero */}
          <div className="text-center space-y-3">
            <div className="flex justify-center items-center gap-4">
              <div className="w-12 h-px bg-primary/40" />
              <span className="text-primary tracking-[0.3em] text-xs font-medium uppercase">
                Invitation
              </span>
              <div className="w-12 h-px bg-primary/40" />
            </div>
            <h1 className="font-serif text-4xl text-white leading-tight">
              {wedding?.title ? (
                <>
                  {t("auth.celebrationPrefix")}
                  <br />
                  <span className="italic text-primary">{wedding.title}</span>
                </>
              ) : (
                <span className="italic text-primary">{t("auth.welcomeTitle")}</span>
              )}
            </h1>
            {weddingMeta}
          </div>

          {/* Glass form panel — fixed gap from hero, not pushed to screen bottom */}
          <div className="glass-panel rounded-xl p-8 mt-10">
            {formContent}
          </div>

          {/* Flexible spacer pushes footer to bottom */}
          <div className="flex-1" />

          <footer className="text-center mt-4">
            <p className="text-white/20 text-[10px] uppercase tracking-[0.2em]">
              {wedding?.title ?? ""}
            </p>
          </footer>
        </main>
      </div>
    );
  }

  // ─── DESKTOP LAYOUT ────────────────────────────────────────────
  return (
    <div className="relative min-h-screen overflow-hidden">
      {background}
      <div className="fixed -top-[10%] -right-[10%] w-[40%] h-[40%] bg-primary/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="fixed -bottom-[10%] -left-[10%] w-[40%] h-[40%] bg-primary/10 blur-[120px] rounded-full pointer-events-none" />
      <main className="relative z-10 min-h-screen flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[1000px] bg-[#1b1817]/80 backdrop-blur-xl border border-primary/20 rounded-xl overflow-hidden shadow-2xl flex">
          {/* Left panel — cover photo or blob background + wedding info */}
          <div className="w-1/2 relative min-h-[520px]">
            {bgAuthUrl ? (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${bgAuthUrl})` }}
              />
            ) : (
              <div className="absolute inset-0 overflow-hidden bg-[#262220]">
                <div style={{ filter: "blur(60px)" }} className="absolute inset-0">
                  <div className="absolute top-[-20%] left-[-20%] w-[80%] h-[80%] rounded-full bg-primary/40" />
                  <div className="absolute bottom-[-20%] right-[-20%] w-[70%] h-[70%] rounded-full bg-primary/25" />
                </div>
                <div className="absolute inset-0 bg-[#1b1817]/30" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#1b1817]/80 to-transparent" />
            <div className="absolute bottom-10 left-10 right-10">
              <h1 className="font-serif text-3xl font-bold tracking-tight text-slate-100">
                {wedding?.title ?? t("auth.welcomeTitle")}
              </h1>
              {formattedDate && (
                <p className="text-primary font-medium mt-2">{formattedDate}</p>
              )}
              {wedding?.location && (
                <div className="flex items-center gap-2 text-white/60 text-sm mt-1">
                  <MapPin className="w-3 h-3 shrink-0" />
                  <span>{wedding.location}</span>
                </div>
              )}
            </div>
          </div>
          {/* Right form panel */}
          <div className="flex-1 p-12 flex flex-col justify-center bg-[#1b1817]/90">
            <div className="max-w-md mx-auto w-full">
              {formContent}
            </div>
          </div>
        </div>
        <footer className="absolute bottom-4 left-0 right-0 text-center pointer-events-none">
          <p className="text-white/20 text-xs tracking-widest uppercase">
            {wedding?.title ?? ""}
          </p>
        </footer>
      </main>
    </div>
  );
}
